import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { RemixContentComponentDefinition } from '@atlassian/malleable-ui-components/types';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { useIsSelectionUnremixable } from '../hooks/use-is-selection-unremixable';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

type RemixToolbarEvent = {
	action: 'clicked';
	actionSubject: 'button';
	actionSubjectId: 'remix';
	attributes: {
		remixSource: 'commentPrimaryToolbar';
	};
	eventType: 'ui';
};

export function RemixCommentPrimaryMenuItem({
	api,
	isDisabled,
	remixDisabledReason,
	isSelectionRemixable,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isSelectionRemixable?: RemixContentComponentDefinition['isSelectionRemixable'];
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element | null {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	const { isRemixing, areAiEntryPointsDisabled } = useSharedPluginStateWithSelector(
		api,
		['malleableUi', 'aiExperience'],
		(states) => ({
			isRemixing: states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
		}),
	);

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled;
	const isSelectionUnremixable = useIsSelectionUnremixable({
		api,
		isSelectionRemixable,
		isSurfaceAvailable: !effectiveDisabled && !isRemixing,
	});

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}

		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		api?.analytics?.actions.fireAnalyticsEvent<RemixToolbarEvent, 'customEventType'>({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'remix',
			eventType: 'ui',
			attributes: {
				remixSource: 'commentPrimaryToolbar',
			},
		});
		const featureArea = getRemixFeatureArea({});
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'commentPrimaryToolbar',
				...(featureArea !== undefined && { featureArea }),
			},
		});
		startRemixCreateExperience({
			experienceTracker,
			attributes: { entryPoint: 'editor', remixSource: 'commentPrimaryToolbar' },
		});
		api?.core.actions.execute(
			api?.malleableUi?.commands.openRemixModal({
				metadata: { interactionSource: 'remixModalFromToolbar' },
				source: 'commentPrimaryToolbar',
			}),
		);
	}, [api, effectiveDisabled, isRemixing, experienceTracker]);

	const label = formatMessage(messages.remixToolbarLabel);

	const tooltipContent = useMemo(() => {
		if (isRemixing) {
			return formatMessage(messages.remixInProgressTooltip);
		}
		if (effectiveDisabled) {
			return getRemixDisabledTooltip(formatMessage, remixDisabledReason);
		}
		return formatMessage(messages.remixToolbarTooltip);
	}, [isRemixing, effectiveDisabled, formatMessage, remixDisabledReason]);

	if (isSelectionUnremixable) {
		return null;
	}
	return (
		<ToolbarDropdownItem
			elemBefore={<AiGenerativeRemixIcon size="small" label={label} />}
			elemAfter={<div />}
			testId="dropdown-item__malleable-ui-remix-comment"
			isDisabled={effectiveDisabled || isRemixing}
			title={tooltipContent}
			onClick={handleClick}
		>
			{label}
		</ToolbarDropdownItem>
	);
}
